<template>
    <b-overlay
        variant="white"
        :show="loading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <div>
            <b-row class="mb-2">
                <b-col>
                    <h3 class="mb-0">Research Discovery Request</h3>
                </b-col>
                <b-col class="text-right">
                    <b-button
                        variant="outline-secondary"
                        class="mr-1"
                        :disabled="!request || !request.productId"
                        @click="openProduct"
                    >
                        Open product
                    </b-button>
                    <b-button variant="outline-primary" @click="goBack">
                        Back to list
                    </b-button>
                </b-col>
            </b-row>

            <b-row v-if="request" class="mt-2">
                <b-col md="6">
                    <HamletTable
                        :columns="detailColumns"
                        :fetch="fetchBusinessDetails"
                        :paging="false"
                        :show-paging-when-empty="false"
                        :show-empty-message="false"
                        :search="false"
                        table-container-class="table-responsive hamlet-table"
                    >
                        <template #header-label>
                            Business data
                        </template>
                        <template #cell-value="data">
                            <b-badge v-if="data.item.key === 'status'" :variant="statusVariant(request.status)">
                                {{ request.status }}
                            </b-badge>
                            <span v-else>{{ data.item.value }}</span>
                        </template>
                    </HamletTable>
                </b-col>
                <b-col md="6">
                    <HamletTable
                        :columns="detailColumns"
                        :fetch="fetchTechnicalDetails"
                        :paging="false"
                        :show-paging-when-empty="false"
                        :show-empty-message="false"
                        :search="false"
                        table-container-class="table-responsive hamlet-table"
                    >
                        <template #header-label>
                            Technical data
                        </template>
                    </HamletTable>
                </b-col>
            </b-row>

            <div v-if="request" class="mt-2">
                <HamletTable
                    :columns="artifactFields"
                    :fetch="fetchArtifacts"
                    :show-paging-when-empty="false"
                    :show-empty-message="true"
                    :search="false"
                    table-container-class="table-responsive hamlet-table"
                >
                    <template #header-label>
                        Artifacts
                    </template>
                    <template #cell-actions="data">
                        <div class="d-flex align-items-center">
                            <b-button
                                size="sm"
                                variant="flat-primary"
                                class="mr-50"
                                @click.stop="downloadArtifact(data.item)"
                            >
                                <feather-icon icon="DownloadIcon" size="14"/>
                            </b-button>
                            <b-button
                                size="sm"
                                variant="flat-secondary"
                                class="mr-50"
                                @click.stop="previewArtifact(data.item)"
                            >
                                <feather-icon icon="EyeIcon" size="14"/>
                            </b-button>
                            <b-button
                                v-if="canOpenArtifactFeedback(data.item)"
                                size="sm"
                                variant="flat-success"
                                @click.stop="openFeedbackModal(data.item)"
                            >
                                <feather-icon icon="MessageSquareIcon" size="14"/>
                            </b-button>
                        </div>
                    </template>
                    <template #cell-feedback="data">
                        <b-badge
                            v-if="getArtifactFeedbackLabel(data.item)"
                            :variant="getArtifactFeedbackVariant(data.item)"
                        >
                            {{ getArtifactFeedbackLabel(data.item) }}
                        </b-badge>
                        <span v-else>-</span>
                    </template>
                    <template #cell-createdAt="data">
                        {{ formatDate(data.item.createdAt) }}
                    </template>
                    <template #no-records>
                        No artifacts
                    </template>
                </HamletTable>
            </div>

            <ArtifactPreviewModal
                ref="artifactPreview"
                :artifact="previewArtifactFile"
                :download-url="previewArtifactUrl"
            />

            <b-modal
                v-model="feedbackModalVisible"
                title="Artifact feedback"
                centered
                :ok-disabled="feedbackSubmitting || !isFeedbackFormValid"
                :cancel-disabled="feedbackSubmitting"
                ok-title="Send feedback"
                cancel-variant="outline-secondary"
                @ok="submitFeedback"
                @hidden="resetFeedbackModal"
            >
                <b-alert v-if="feedbackError" variant="danger" show>
                    {{ feedbackError }}
                </b-alert>

                <div v-if="feedbackArtifact" class="mb-1">
                    <strong>{{ feedbackArtifact.filename }}</strong>
                </div>

                <b-form-group
                    v-for="control in feedbackControls"
                    :key="control.code"
                    :label="control.label"
                >
                    <b-form-radio-group
                        v-model="feedbackValues[control.code]"
                        :options="feedbackControlOptions(control)"
                        buttons
                        button-variant="outline-primary"
                    />
                </b-form-group>
            </b-modal>
        </div>
    </b-overlay>
</template>

<script>
import {
    BOverlay,
    BRow,
    BCol,
    BButton,
    BBadge,
    BModal,
    BFormGroup,
    BFormRadioGroup,
    BAlert,
} from 'bootstrap-vue'
import {ResearchDiscoveryApi} from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import dateRenderer from '@core/mixins/date-renderer'
import {HamletTable} from '@core/components/common'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ArtifactPreviewModal from '@/views/research/discovery/components/ArtifactPreviewModal.vue'

export default {
    components: {
        BOverlay,
        BRow,
        BCol,
        BButton,
        BBadge,
        BModal,
        BFormGroup,
        BFormRadioGroup,
        BAlert,
        HamletTable,
        // eslint-disable-next-line vue/no-unused-components
        ToastificationContent,
        ArtifactPreviewModal,
    },
    mixins: [backendResponse, dateRenderer],
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            request: null,
            previewArtifactFile: null,
            previewArtifactUrl: '',
            previewArtifactObjectUrl: null,
            feedbackModalVisible: false,
            feedbackArtifact: null,
            feedbackValues: {},
            feedbackSubmitting: false,
            feedbackError: '',
            feedbackStatusTimeout: null,
            detailColumns: [
                {key: 'label', label: 'Field'},
                {key: 'value', label: 'Value'},
            ],
            businessFields: [
                {key: 'status', label: 'Status'},
                {key: 'productId', label: 'Product ID'},
                {key: 'productIdentifier', label: 'Product Identifier'},
                {key: 'productName', label: 'Product Name'},
                {key: 'supplierName', label: 'Supplier Name'},
                {key: 'supplierDomain', label: 'Supplier Domain'},
                {key: 'discoveryDuration', label: 'Discovery Duration'},
            ],
            technicalFields: [
                {key: 'requestId', label: 'Request ID'},
                {key: 'correlationId', label: 'Correlation ID'},
                {key: 'workflowStepExecutionId', label: 'Workflow Step Execution ID'},
                {key: 'engineId', label: 'Engine'},
                {key: 'providerJobId', label: 'Provider Job ID'},
                {key: 'attemptsCount', label: 'Attempts'},
                {key: 'lastErrorCode', label: 'Last Error Code'},
                {key: 'lastErrorMessage', label: 'Last Error Message'},
                {key: 'createdAt', label: 'Created At'},
                {key: 'updatedAt', label: 'Updated At'},
                {key: 'completedAt', label: 'Completed At'},
                {key: 'failedAt', label: 'Failed At'},
            ],
            artifactFields: [
                {key: 'actions', label: ''},
                {key: 'feedback', label: 'Feedback'},
                {key: 'fileRefId', label: 'File Ref ID'},
                {key: 'filename', label: 'Filename'},
                {key: 'mimeType', label: 'MIME Type'},
                {key: 'size', label: 'Size'},
                {key: 'checksum', label: 'Checksum'},
                {key: 'namespace', label: 'Namespace'},
                {key: 'type', label: 'Type'},
                {key: 'createdAt', label: 'Created At'},
            ],
        }
    },
    methods: {
        goBack() {
            this.$router.push({name: 'research-discovery-grid'})
        },
        openProduct() {
            if (!this.request || !this.request.productId) {
                return
            }

            this.$router.push({name: 'product-card', params: {id: this.request.productId}})
        },
        valueByKey(key) {
            if (!this.request) {
                return '-'
            }

            if (key === 'discoveryDuration') {
                return this.getDiscoveryDuration()
            }

            const value = this.request[key]

            if (key.endsWith('At')) {
                return this.formatDate(value)
            }

            if (value === null || value === undefined || value === '') {
                return '-'
            }

            if (typeof value === 'object') {
                return JSON.stringify(value)
            }

            return value
        },
        formatDate(value) {
            if (!value) {
                return '-'
            }

            return this.renderDateTime(value)
        },
        statusVariant(status) {
            const map = {
                created: 'secondary',
                dispatched: 'info',
                processing: 'warning',
                completed: 'success',
                failed: 'danger',
            }

            return map[status] || 'secondary'
        },
        getDiscoveryDuration() {
            const startedAt = this.request && this.request.createdAt ? this.request.createdAt : null
            const finishedAt = this.request && (this.request.completedAt || this.request.failedAt)
                ? (this.request.completedAt || this.request.failedAt)
                : null

            if (!startedAt || !finishedAt) {
                return '-'
            }

            const diffMs = new Date(finishedAt).getTime() - new Date(startedAt).getTime()
            if (!Number.isFinite(diffMs) || diffMs < 0) {
                return '-'
            }

            const totalSeconds = Math.floor(diffMs / 1000)
            const days = Math.floor(totalSeconds / 86400)
            const hours = Math.floor((totalSeconds % 86400) / 3600)
            const minutes = Math.floor((totalSeconds % 3600) / 60)
            const seconds = totalSeconds % 60

            const parts = []
            if (days > 0) {
                parts.push(`${days}d`)
            }
            if (hours > 0 || days > 0) {
                parts.push(`${hours}h`)
            }
            if (minutes > 0 || hours > 0 || days > 0) {
                parts.push(`${minutes}m`)
            }
            parts.push(`${seconds}s`)

            return parts.join(' ')
        },
        load() {
            this.loading = true

            return ResearchDiscoveryApi
                .getRequest(this.id)
                .then((response) => {
                    this.request = response.data
                })
                .catch((e) => {
                    this.handleBackendError(e)
                    this.goBack()
                })
                .finally(() => {
                    this.loading = false
                })
        },
        fetchArtifacts(_, cb) {
            const items = this.request && this.request.artifacts ? this.request.artifacts : []
            cb(items.length, items)
        },
        fetchBusinessDetails(_, cb) {
            cb(this.businessDetails.length, this.businessDetails)
        },
        fetchTechnicalDetails(_, cb) {
            cb(this.technicalDetails.length, this.technicalDetails)
        },
        getArtifactDownloadUrl(artifact) {
            return ResearchDiscoveryApi.getArtifactDownloadUrl(artifact)
        },
        downloadArtifact(artifact) {
            return ResearchDiscoveryApi
                .downloadArtifactFile(artifact)
                .then((response) => {
                    const mimeType = artifact && artifact.mimeType ? artifact.mimeType : 'application/octet-stream'
                    const objectUrl = URL.createObjectURL(new Blob([response.data], {type: mimeType}))
                    const link = document.createElement('a')
                    link.href = objectUrl
                    link.download = artifact && artifact.filename ? artifact.filename : 'artifact'
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(objectUrl)
                })
                .catch((e) => {
                    this.handleBackendError(e)
                })
        },
        previewArtifact(artifact) {
            return ResearchDiscoveryApi
                .downloadArtifactFile(artifact)
                .then((response) => {
                    if (this.previewArtifactObjectUrl) {
                        URL.revokeObjectURL(this.previewArtifactObjectUrl)
                        this.previewArtifactObjectUrl = null
                    }

                    const mimeType = artifact && artifact.mimeType ? artifact.mimeType : 'application/octet-stream'
                    const objectUrl = URL.createObjectURL(new Blob([response.data], {type: mimeType}))
                    this.previewArtifactObjectUrl = objectUrl
                    this.previewArtifactFile = artifact
                    this.previewArtifactUrl = objectUrl
                    this.$nextTick(() => {
                        this.$refs.artifactPreview.show()
                    })
                })
                .catch((e) => {
                    this.handleBackendError(e)
                })
        },
        canSubmitArtifactFeedback(artifact) {
            return artifact
                && artifact.feedbackAvailable === true
                && this.selectedFeedbackPayloadType
        },
        canOpenArtifactFeedback(artifact) {
            return this.canSubmitArtifactFeedback(artifact)
                && !['pending', 'sent'].includes(this.getArtifactFeedbackStatus(artifact))
        },
        getArtifactFeedbackStatus(artifact) {
            return artifact && artifact.feedbackState && artifact.feedbackState.status
                ? artifact.feedbackState.status
                : null
        },
        getArtifactFeedbackLabel(artifact) {
            const status = this.getArtifactFeedbackStatus(artifact)
            if (status) {
                return status
            }

            return this.canSubmitArtifactFeedback(artifact) ? 'not sent' : ''
        },
        getArtifactFeedbackVariant(artifact) {
            const map = {
                pending: 'warning',
                sent: 'success',
                failed: 'danger',
            }
            const status = this.getArtifactFeedbackStatus(artifact)

            return map[status] || 'secondary'
        },
        openFeedbackModal(artifact) {
            this.feedbackArtifact = artifact
            this.feedbackValues = {}
            this.feedbackError = ''

            this.feedbackControls.forEach((control) => {
                this.$set(this.feedbackValues, control.code, null)
            })

            this.feedbackModalVisible = true
        },
        resetFeedbackModal() {
            if (this.feedbackSubmitting) {
                return
            }

            this.feedbackArtifact = null
            this.feedbackValues = {}
            this.feedbackError = ''
        },
        feedbackControlOptions(control) {
            const values = control && Array.isArray(control.values) ? control.values : []

            return values.map(value => ({
                text: value.label,
                value: value.value,
            }))
        },
        buildFeedbackPayload() {
            const payloadType = this.selectedFeedbackPayloadType

            return {
                payloadType: payloadType.type,
                data: {
                    features: this.feedbackControls.map(control => ({
                        code: control.code,
                        value: this.feedbackValues[control.code],
                    })),
                },
            }
        },
        submitFeedback(event) {
            event.preventDefault()

            if (!this.request || !this.feedbackArtifact || this.feedbackSubmitting || !this.isFeedbackFormValid) {
                return
            }

            this.feedbackError = ''
            this.feedbackSubmitting = true

            return ResearchDiscoveryApi
                .submitArtifactFeedback(
                    this.request.requestId,
                    this.feedbackArtifact.fileRefId,
                    this.buildFeedbackPayload()
                )
                .then((response) => {
                    this.feedbackModalVisible = false
                    this.showDefaultSuccessMessage('Feedback has been accepted and is being processed.')

                    const attemptId = response && response.data ? response.data.attemptId : null
                    if (attemptId) {
                        this.setArtifactFeedbackState(this.feedbackArtifact, {
                            latestAttemptId: attemptId,
                            status: 'pending',
                            payloadType: this.selectedFeedbackPayloadType.type,
                            createdAt: null,
                            sentAt: null,
                            failedAt: null,
                            errorMessage: null,
                        })
                        this.scheduleFeedbackStatusCheck(attemptId)
                    }
                })
                .catch((e) => {
                    if (e && e.response && (e.response.status === 409 || e.response.status === 422)) {
                        this.feedbackError = e.response.data && e.response.data.detail
                            ? e.response.data.detail
                            : 'Feedback cannot be submitted for this artifact.'
                        return
                    }

                    this.handleBackendError(e)
                })
                .finally(() => {
                    this.feedbackSubmitting = false
                })
        },
        scheduleFeedbackStatusCheck(attemptId) {
            if (this.feedbackStatusTimeout) {
                clearTimeout(this.feedbackStatusTimeout)
            }

            this.feedbackStatusTimeout = setTimeout(() => {
                this.feedbackStatusTimeout = null
                this.loadFeedbackAttemptStatus(attemptId)
            }, 6000)
        },
        loadFeedbackAttemptStatus(attemptId) {
            if (!this.request || !this.request.requestId) {
                return Promise.resolve()
            }

            return ResearchDiscoveryApi
                .getFeedbackAttempt(this.request.requestId, attemptId)
                .then((response) => {
                    this.updateArtifactFeedbackStateFromAttempt(response.data)
                    this.showFeedbackAttemptToast(response.data)
                })
                .catch((e) => {
                    this.handleBackendError(e)
                })
        },
        showFeedbackAttemptToast(attempt) {
            const status = attempt && attempt.status ? attempt.status : 'pending'
            const map = {
                sent: {
                    title: 'Feedback sent',
                    text: 'Artifact feedback has been sent to the provider.',
                    icon: 'CheckIcon',
                    variant: 'success',
                },
                failed: {
                    title: 'Feedback failed',
                    text: attempt && attempt.errorMessage ? attempt.errorMessage : 'Provider feedback delivery failed.',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                },
                pending: {
                    title: 'Feedback processing',
                    text: 'Artifact feedback is still being processed.',
                    icon: 'ClockIcon',
                    variant: 'warning',
                },
            }
            const config = map[status] || map.pending

            this.$toast({
                component: ToastificationContent,
                props: config,
            })
        },
        setArtifactFeedbackState(artifact, feedbackState) {
            if (!artifact) {
                return
            }

            this.$set(artifact, 'feedbackState', feedbackState)
        },
        updateArtifactFeedbackStateFromAttempt(attempt) {
            const fileReferenceId = attempt && attempt.target ? attempt.target.fileReferenceId : null
            const artifacts = this.request && Array.isArray(this.request.artifacts) ? this.request.artifacts : []
            const artifact = artifacts.find(item => item && item.fileRefId === fileReferenceId)
            if (!artifact) {
                return
            }

            this.setArtifactFeedbackState(artifact, {
                latestAttemptId: attempt.attemptId,
                status: attempt.status,
                payloadType: attempt.payloadType,
                createdAt: attempt.createdAt || null,
                sentAt: attempt.sentAt || null,
                failedAt: attempt.failedAt || null,
                errorMessage: attempt.errorMessage || null,
            })
        },
    },
    beforeDestroy() {
        if (this.previewArtifactObjectUrl) {
            URL.revokeObjectURL(this.previewArtifactObjectUrl)
            this.previewArtifactObjectUrl = null
        }
        if (this.feedbackStatusTimeout) {
            clearTimeout(this.feedbackStatusTimeout)
            this.feedbackStatusTimeout = null
        }
    },
    computed: {
        businessDetails() {
            return this.businessFields.map((field) => ({
                key: field.key,
                label: field.label,
                value: this.valueByKey(field.key),
            }))
        },
        technicalDetails() {
            return this.technicalFields.map((field) => ({
                key: field.key,
                label: field.label,
                value: this.valueByKey(field.key),
            }))
        },
        selectedFeedbackPayloadType() {
            const capabilities = this.request && this.request.feedbackCapabilities
                ? this.request.feedbackCapabilities
                : null
            const payloadTypes = capabilities && capabilities.available === true && Array.isArray(capabilities.payloadTypes)
                ? capabilities.payloadTypes
                : []

            return payloadTypes.find(payloadType => payloadType && payloadType.type === 'document_features') || null
        },
        feedbackControls() {
            const payloadType = this.selectedFeedbackPayloadType

            return payloadType && Array.isArray(payloadType.controls) ? payloadType.controls : []
        },
        isFeedbackFormValid() {
            return this.feedbackControls.length > 0
                && this.feedbackControls.every(control => this.feedbackValues[control.code])
        },
    },
    created() {
        this.load()
    },
}
</script>
